/**
 * Servicio de Invitaciones
 * Genera y gestiona invitaciones de registro para dueños y gerentes
 */

import { apiFetch } from "./apiClient";

const AUTH_API = import.meta.env.VITE_AUTH_API_URL || "http://localhost:5010/api/v1";

export interface Invitation {
  id: string;
  token: string;
  company_id: string;
  company_name?: string;
  branch_id?: string | null;
  role_id: number;
  role_name?: string;
  modules_config: Record<string, unknown>;
  invitation_url: string;
  created_by?: string | null;
  expires_at: string;
  used_at?: string | null;
  is_used: boolean;
  created_at?: string;
}

export interface CreateInvitationPayload {
  company_id: string;
  /** UUID de la sucursal (obligatorio para role_id=3 gerente) */
  branch_id?: string;
  modules_config?: Record<string, unknown>;
  /** 2 = dueño (default), 3 = gerente */
  role_id?: number;
  /** Days until expiry, default 7 */
  expires_days?: number;
}

/**
 * Crea una nueva invitación para un dueño o gerente.
 * Requiere autenticación (sysadmin o dueño).
 */
export async function createInvitation(
  payload: CreateInvitationPayload
): Promise<Invitation> {
  const res = await apiFetch(`${AUTH_API}/invitations/`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.message || "Error al generar la invitación");
  return json.data as Invitation;
}

/**
 * Obtiene información pública de una invitación por token.
 * Endpoint público, sin autenticación.
 */
export async function getInvitationByToken(
  token: string
): Promise<Invitation> {
  const res = await fetch(`${AUTH_API}/invitations/${token}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.message || "Invitación no válida");
  return json.data as Invitation;
}

export interface AcceptInvitationPayload {
  username: string;
  password: string;
  first_name: string;
  last_name: string;
  email?: string;
}

/**
 * El invitado completa su registro usando el token.
 * Endpoint público, sin autenticación.
 */
export async function acceptInvitation(
  token: string,
  payload: AcceptInvitationPayload
): Promise<{ user_id: string; username: string }> {
  const res = await fetch(`${AUTH_API}/invitations/${token}/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.message || "Error al completar el registro");
  return json.data as { user_id: string; username: string };
}

/**
 * Lista las invitaciones de una empresa (sysadmin o dueño).
 */
export async function listInvitationsByCompany(
  companyId: string
): Promise<Invitation[]> {
  const res = await apiFetch(`${AUTH_API}/invitations/company/${companyId}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.message || "Error al obtener invitaciones");
  return json.data as Invitation[];
}
